import { Button } from '@chakra-ui/react';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import {
  useInteractiveComponentStateQuery,
  useSaveInteractiveComponentStateMutation,
} from '../../../../../generated/graphqlModels';
import { MD5 } from '../../../../../helpers/utils';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import MarkTheWords from '../../../../app/pages/InteractiveTest/components/MarkTheWords';
import { GET_INTERACTIVE_COMPONENT_STATE } from '../../../../app/pages/InteractiveTest/components/shared/queries';
import SelectUser from '../../SelectUser';
import { PluginProps } from '../Plugin';

const MarkTheWordsPlugin = (props: PluginProps) => {
  const { content, studyGroup } = props;
  const { t } = useTranslation();
  const contentData = content.data;
  const user = useContext(AuthContext);
  const isMobile = useIsMobile();
  const debugParam = useQueryParam('debug');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    user?.pk || null,
  );
  const [showCorrect, setShowCorrect] = useState(false);

  const debug = user?.roles.includes(Role.staff) && !!debugParam;

  const studyGroupId = studyGroup.pk;

  const queryVariables = {
    userId: selectedUserId! || user?.pk!,
    studyGroupId: studyGroupId!,
    componentId: contentData.unique_id,
  };
  const {
    data,
    // loading
  } = useInteractiveComponentStateQuery({
    variables: queryVariables,
    skip: user == null || user.pk == null,
  });

  const [
    saveState,
    // { data: mutData, loading: mutLoading, error: mutError }
  ] = useSaveInteractiveComponentStateMutation({
    refetchQueries: [
      {
        query: GET_INTERACTIVE_COMPONENT_STATE,
        variables: queryVariables,
      },
    ],
  });

  const hash = useMemo(() => {
    return MD5.hash(JSON.stringify(contentData.config));
  }, [contentData.config]);

  const submit = (incorrect: number, total: number, state: any) => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state,
        },
        reset: false,
      },
    });
  };

  const reset = () => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state: '',
        },
        reset: true,
      },
    });
  };

  const isViewOnly = !!selectedUserId && selectedUserId !== user?.pk!;

  const ShowCorrect = () =>
    user?.roles.includes(Role.teacher) ? (
      <Button onClick={() => setShowCorrect(!showCorrect)}>
        {showCorrect ? t('plugin.hideCorrect') : t('plugin.showCorrect')}
      </Button>
    ) : null;

  return (
    <>
      <SelectUser
        studyGroupId={studyGroupId}
        studentId={selectedUserId}
        setStudentId={setSelectedUserId}
      />
      <div
        style={
          isMobile
            ? {
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
              }
            : undefined
        }
      >
        <MarkTheWords
          key={contentData.id}
          text={contentData.text}
          words={contentData.config}
          debug={debug}
          submit={submit}
          reset={reset}
          initialState={data?.getInteractiveContentState?.state}
          isViewOnly={isViewOnly}
          showCorrect={showCorrect}
        ></MarkTheWords>
        <ShowCorrect />
      </div>
    </>
  );
};

export default MarkTheWordsPlugin;
